import axios from "axios";
import { auth, logAnalyticsEvent } from "./firebase";
import { getKnowledgeItem, searchKnowledgeBase } from "./Knowledge";

/**
 * Configuración del servicio de chatbot
 * Mantiene compatibilidad con el sistema existente
 */
// Configuración
let functionsBaseUrl;
if (process.env.NODE_ENV === "development") {
  // --- ESTO SE USA EN LOCAL CON 'npm start' ---
  functionsBaseUrl = "http://localhost:5001/vicdown-3bd70/europe-west1/api";
  console.warn(
    "Usando URL del Emulador de Firebase Functions:",
    functionsBaseUrl,
  );
} else {
  // --- ESTO SE USA EN PRODUCCIÓN (DESPUÉS DE 'npm run build') ---
  functionsBaseUrl =
    process.env.REACT_APP_API_URL ||
    "https://europe-west1-vicdown-3bd70.cloudfunctions.net/api";
}

const API_URL = functionsBaseUrl; // API_URL tendrá el valor correcto según el entorno
const TIMEOUT = 60000; // 60 segundos de timeout
const CACHE_TTL = 1000 * 60 * 60; // 1 hora para caché
const MAX_RETRIES = 2; // Número máximo de reintentos para peticiones fallidas
const RETRY_DELAY = 1000; // Milisegundos entre reintentos

/**
 * Cliente axios configurado con interceptores robustos
 * @type {AxiosInstance}
 */
const apiClient = axios.create({
  baseURL: API_URL, // Usa la URL correcta automáticamente
  timeout: TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para añadir token de autenticación en cada solicitud
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Añadir información para accesibilidad si se solicita
      if (config.data && config.data.accessibilityPreferences) {
        config.headers["X-Accessibility-Preferences"] = JSON.stringify(
          config.data.accessibilityPreferences,
        );
      }

      return config;
    } catch (error) {
      console.error("Error en interceptor de autenticación:", error);
      // Continuar sin token en caso de error
      return config;
    }
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para manejar respuestas
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // No reintentar si ya hemos alcanzado el máximo de intentos
    if (originalRequest._retry >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    // Reintentar en caso de errores de red específicos
    if (
      (error.code === "ECONNABORTED" ||
        error.message.includes("timeout") ||
        (error.response &&
          (error.response.status === 408 ||
            error.response.status === 429 ||
            error.response.status >= 500)) ||
        !error.response) &&
      !originalRequest._retry
    ) {
      originalRequest._retry = (originalRequest._retry || 0) + 1;

      // Esperar antes de reintentar (backoff exponencial)
      const delay = RETRY_DELAY * 2 ** (originalRequest._retry - 1);

      return new Promise((resolve) => {
        setTimeout(() => {
          console.log(
            `Reintentando petición (${originalRequest._retry}/${MAX_RETRIES})...`,
          );
          resolve(apiClient(originalRequest));
        }, delay);
      });
    }

    return Promise.reject(error);
  },
);

/**
 * Sistema de caché mejorado con límite de tamaño y TTL
 * @type {Map}
 */
const responseCache = new Map();
const MAX_CACHE_SIZE = 100; // Limitar tamaño para evitar consumo excesivo de memoria

/**
 * Limpia entradas antiguas o excesivas en la caché
 * @private
 */
const cleanupCache = () => {
  // Si el tamaño excede el límite, eliminar entradas más antiguas
  if (responseCache.size > MAX_CACHE_SIZE) {
    // Convertir a array para ordenar por timestamp
    const cacheEntries = Array.from(responseCache.entries()).sort(
      (a, b) => a[1].timestamp - b[1].timestamp,
    );

    // Eliminar entradas antiguas hasta alcanzar el 75% del tamaño máximo
    while (responseCache.size > MAX_CACHE_SIZE * 0.75) {
      const oldestKey = cacheEntries.shift()?.[0];
      if (oldestKey) responseCache.delete(oldestKey);
    }
  }

  // También eliminar entradas expiradas
  const now = Date.now();
  for (const [key, entry] of responseCache.entries()) {
    if (now - entry.timestamp > CACHE_TTL) {
      responseCache.delete(key);
    }
  }
};

// Limpiar caché periódicamente
if (typeof window !== "undefined") {
  setInterval(cleanupCache, CACHE_TTL / 4); // Limpiar cada cuarto del TTL
}

/**
 * Normaliza un texto para uso en claves de caché
 * @param {string} text - Texto a normalizar
 * @returns {string} - Texto normalizado
 * @private
 */
const normalizeText = (text) => {
  return (text || "").trim().toLowerCase().replace(/\s+/g, " ").slice(0, 100); // Limitar longitud para claves de caché
};

/**
 * Busca información relevante en la base de conocimientos para enriquecer respuestas
 * @param {string} query - Consulta para buscar
 * @param {string} language - Idioma (es, en, etc.)
 * @returns {Promise<Object>} - Información contextual encontrada
 */
export const findRelevantKnowledge = async (query, language = "es") => {
  try {
    // Usar la función existente del servicio Knowledge
    const results = await searchKnowledgeBase(query, language, 5, true);

    if (!results || results.length === 0) {
      return { found: false, context: "" };
    }

    // Construir contexto enriquecido con los resultados encontrados
    let contextData = "";
    let primaryInfo = null;

    // Si tenemos un resultado muy relevante, obtener su contenido completo
    if (results[0]?.relevance >= 0.7 && results[0]?.id) {
      try {
        primaryInfo = await getKnowledgeItem(results[0].id, language);
      } catch (e) {
        console.warn("Error fetching primary knowledge item:", e);
      }
    }

    // Construir contexto con la información más relevante primero
    if (primaryInfo?.content) {
      contextData += `Información principal: ${primaryInfo.content}\n\n`;
    }

    // Añadir resúmenes de otros resultados relevantes
    results.forEach((item, index) => {
      if (index === 0 && primaryInfo) return; // Saltamos el primero si ya lo incluimos completo
      if (item.summary) {
        contextData += `Información adicional ${index + 1}: ${item.summary}\n`;
      } else if (item.content) {
        // Si no hay resumen, usar parte del contenido
        contextData += `Información adicional ${
          index + 1
        }: ${item.content.substring(0, 200)}...\n`;
      }
    });

    return {
      found: contextData.length > 0,
      context: contextData,
      primarySource: primaryInfo?.title || results[0]?.title,
      results: results.map((r) => ({ id: r.id, title: r.title })),
    };
  } catch (error) {
    console.error("Error buscando conocimiento relevante:", error);
    return { found: false, context: "", error: error.message };
  }
};

/**
 * Obtiene una respuesta del chatbot con manejo de errores mejorado
 * @param {string} message - Mensaje del usuario
 * @param {string} [context=''] - Contexto de la conversación
 * @param {string} [language='es'] - Idioma (por defecto español)
 * @param {Object} [options={}] - Opciones adicionales
 * @param {Object} [options.accessibilityPreferences] - Preferencias de accesibilidad
 * @param {boolean} [options.simplifiedLanguage=false] - Usar lenguaje simplificado
 * @param {boolean} [options.textToSpeech=false] - Incluir audio para respuesta
 * @returns {Promise<Object>} - Respuesta del chatbot
 */
export const getChatbotResponse = async (
  message,
  context = "",
  language = "es",
  options = {},
) => {
  try {
    // Buscar información relevante en la base de conocimientos
    const knowledgeInfo = await findRelevantKnowledge(message, language);

    // Añadir la información encontrada al contexto para obtener respuestas más precisas
    let enrichedContext = context;
    if (knowledgeInfo.found) {
      enrichedContext = `${enrichedContext}\n\nInformación relevante de nuestra base de datos: ${knowledgeInfo.context}`;
    }

    // Registrar uso para analíticas (anónimo)
    try {
      logAnalyticsEvent?.("chatbot_query", {
        language,
        messageLength: message?.length,
        hasContext: Boolean(context),
        knowledgeFound: knowledgeInfo.found,
        timestamp: new Date().toISOString(),
      });
    } catch (e) {
      // Silenciar errores de analítica para no interrumpir funcionalidad
    }

    // Mostrar mensaje para desarrolladores en consola
    console.log("📡 Intentando conexión con API:", API_URL);

    const response = await apiClient.post("/chat", {
      message,
      context: enrichedContext, // Usamos el contexto enriquecido
      language,
      ...options,
    });

    console.log("✅ Respuesta recibida del servidor");

    // Validar respuesta
    if (!response.data || !response.data.response) {
      const errorMessage =
        "El servidor devolvió una respuesta inválida o vacía";
      console.error(errorMessage);
      throw new Error(errorMessage);
    }

    // La respuesta del servidor siempre es un string.
    // Si hay knowledge sources, las adjuntamos como metadatos sin
    // convertir la respuesta en objeto (eso causaba [object Object]).
    const responseText =
      typeof response.data.response === "string"
        ? response.data.response
        : String(response.data.response);

    return responseText;
  } catch (error) {
    console.error("Error al obtener respuesta del chatbot:", error);

    // Mensajes de error más informativos para diferentes situaciones
    let errorMessage;

    if (!navigator.onLine) {
      errorMessage =
        "No hay conexión a internet. Por favor, verifica tu conexión e inténtalo de nuevo.";
    } else if (
      error.code === "ECONNABORTED" ||
      error.message?.includes("timeout")
    ) {
      errorMessage =
        "Tiempo de espera agotado. La respuesta está tardando demasiado. Por favor, inténtalo de nuevo con una pregunta más corta.";
    } else if (error.response?.status === 429) {
      errorMessage =
        "Has realizado demasiadas consultas en poco tiempo. Por favor, espera unos minutos antes de intentarlo de nuevo.";
    } else if (
      error.response?.status === 401 ||
      error.response?.status === 403
    ) {
      errorMessage =
        "No tienes autorización para usar este servicio. Por favor, inicia sesión nuevamente.";
    } else if (error.response?.status >= 500) {
      errorMessage =
        "El servicio de chatbot está experimentando problemas temporales. Por favor, inténtalo más tarde.";
    } else {
      // Usar el mensaje de error del servidor si está disponible
      errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Ocurrió un error desconocido al comunicarse con el chatbot";
    }

    throw new Error(errorMessage);
  }
};

/**
 * Obtiene respuesta con caché para mejorar rendimiento
 * Versión mejorada con normalización de consultas y mejor manejo de errores
 * @param {string} message - Mensaje del usuario
 * @param {string} [context=''] - Contexto de la conversación
 * @param {string} [language='es'] - Idioma
 * @param {Object} [options={}] - Opciones adicionales
 * @returns {Promise<string>} - Respuesta del chatbot
 */
export const getCachedChatbotResponse = async (
  message,
  context = "",
  language = "es",
  options = {},
) => {
  // Normalizar inputs para mejor comportamiento de caché
  const normalizedMessage = normalizeText(message);
  const normalizedContext = normalizeText(context);

  // No usar caché para mensajes vacíos
  if (!normalizedMessage) {
    throw new Error("El mensaje no puede estar vacío");
  }

  // No usar caché para opciones especiales de accesibilidad
  if (options.textToSpeech || options.simplifiedLanguage) {
    return getChatbotResponse(message, context, language, options);
  }

  const cacheKey = `${language}:${normalizedContext}:${normalizedMessage}`;

  const cachedItem = responseCache.get(cacheKey);
  if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL) {
    // Registrar uso de caché para analíticas
    try {
      logAnalyticsEvent?.("chatbot_cache_hit", { language });
    } catch (e) {
      // Silenciar errores
    }
    return cachedItem.response;
  }

  try {
    const response = await getChatbotResponse(
      message,
      context,
      language,
      options,
    );

    // Solo guardar en caché si es una respuesta válida
    if (response) {
      responseCache.set(cacheKey, {
        response,
        timestamp: Date.now(),
      });

      // Limpiar caché si es necesario
      if (responseCache.size > MAX_CACHE_SIZE) {
        cleanupCache();
      }
    }

    return response;
  } catch (error) {
    // Para ciertos errores, intentar buscar respuestas similares en caché
    // como un fallback cuando el servidor está inaccesible
    if (!navigator.onLine || error.message.includes("conexión")) {
      // Buscar si hay una respuesta parecida en caché (búsqueda aproximada)
      const keys = Array.from(responseCache.keys()).filter((key) =>
        key.startsWith(`${language}:`),
      );

      for (const key of keys) {
        if (key.includes(normalizedMessage.substring(0, 15))) {
          const cachedItem = responseCache.get(key);
          if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL * 2) {
            return {
              ...cachedItem.response,
              fromCache: true,
              approximateMatch: true,
            };
          }
        }
      }
    }

    // Si no hay respuesta en caché, reenviar el error
    throw error;
  }
};

/**
 * Genera un post para el blog utilizando IA con manejo mejorado de errores
 * @param {Object|string} options - Opciones del post o el tema del post
 * @param {string} options.topic - Tema del post
 * @param {Array<string>} [options.outlinePoints=[]] - Puntos principales
 * @param {string} [options.tone='informativo'] - Tono (informativo, casual, inspirador)
 * @param {string} [options.length='medio'] - Longitud (corto, medio, largo)
 * @param {string} [options.language='es'] - Idioma
 * @param {string} [options.audience='general'] - Audiencia objetivo
 * @param {boolean} [options.includeSummary=true] - Incluir resumen
 * @param {Array<string>} [options.keywords=[]] - Palabras clave para SEO
 * @param {boolean} [options.simplifiedLanguage=false] - Usar lenguaje simplificado para accesibilidad
 * @returns {Promise<Object>} - Contenido del post generado
 */
export const generateBlogPost = async (options) => {
  const {
    topic,
    outlinePoints = [],
    tone = "informativo",
    length = "medio",
    language = "es",
    audience = "general",
    includeSummary = true,
    keywords = [],
    simplifiedLanguage = false,
  } = typeof options === "string"
    ? { topic: options } // Compatibilidad con versión anterior
    : options;

  // Validar entrada
  if (!topic || typeof topic !== "string" || topic.trim().length === 0) {
    throw new Error("Se requiere un tema válido para generar el post");
  }

  try {
    // Añadir información de seguimiento para registro y depuración
    const requestId = `blog-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 9)}`;

    const response = await apiClient.post("/api/generate-blog", {
      topic,
      outlinePoints,
      tone,
      length,
      language,
      audience,
      includeSummary,
      keywords,
      simplifiedLanguage,
      requestId,
    });

    // Validación robusta de la respuesta
    if (!response.data) {
      throw new Error("Respuesta vacía del servidor");
    }

    if (!response.data.content) {
      throw new Error("El contenido generado está vacío");
    }

    // Registrar éxito para análisis
    try {
      logAnalyticsEvent?.("blog_post_generated", {
        language,
        audience,
        contentLength: response.data.content?.length || 0,
      });
    } catch (e) {
      // No interrumpir el flujo si hay error en analítica
    }

    return {
      content: response.data.content,
      title: response.data.title || `Artículo sobre ${topic}`,
      summary: response.data.summary || "",
      keywords: response.data.keywords || keywords,
      metadata: response.data.metadata || {
        readingTime: estimateReadingTime(response.data.content),
        complexity: estimateTextComplexity(response.data.content),
        createdAt: new Date().toISOString(),
        generatedBy: "IA",
      },
    };
  } catch (error) {
    console.error("Error al generar post de blog:", error);

    // Registrar error para análisis
    try {
      logAnalyticsEvent?.("blog_post_generation_failed", {
        errorType: error.code || "unknown",
        errorMessage: error.message,
      });
    } catch (e) {
      // No interrumpir el flujo si hay error en analítica
    }

    let errorMessage = "Error al generar el contenido";

    if (error.code === "ECONNABORTED") {
      errorMessage =
        "La generación del contenido está tomando demasiado tiempo. Intenta con un tema más específico o un post más corto.";
    } else if (!navigator.onLine) {
      errorMessage =
        "No hay conexión a internet. Verifica tu conexión e inténtalo de nuevo.";
    } else if (error.response?.status === 429) {
      errorMessage =
        "Has alcanzado el límite de generación de contenido. Intenta de nuevo más tarde.";
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Implementación robusta de fetchGPT4Response para compatibilidad
 * @param {string} message - Mensaje del usuario
 * @param {string} [context] - Contexto de la conversación
 * @param {string} [language='es'] - Idioma
 * @returns {Promise<string>} - Respuesta del chatbot
 */
export const fetchGPT4Response = async (message, context, language = "es") => {
  try {
    return getChatbotResponse(message, context, language);
  } catch (error) {
    console.error("Error en fetchGPT4Response:", error);
    throw error;
  }
};

/**
 * Genera una imagen para un blog post utilizando IA
 * @param {string} prompt - Descripción de la imagen a generar
 * @param {Object} [options={}] - Opciones de generación
 * @param {string} [options.size='1024x768'] - Tamaño de la imagen
 * @param {string} [options.style='realistic'] - Estilo visual
 * @param {string} [options.subject='síndrome de Down'] - Tema principal
 * @param {string} [options.quality='high'] - Calidad de la imagen
 * @param {boolean} [options.accessibleDescription=true] - Generar descripción accesible
 * @returns {Promise<Object>} - URL de la imagen generada y metadatos
 */
export const generateImage = async (prompt, options = {}) => {
  const {
    size = "1024x768",
    style = "realistic",
    subject = "síndrome de Down",
    quality = "high",
    accessibleDescription = true,
  } = options;

  // Validar prompt
  if (!prompt || typeof prompt !== "string" || prompt.trim().length < 5) {
    throw new Error(
      "Se requiere una descripción detallada para generar la imagen",
    );
  }

  try {
    // Validar tamaños permitidos (evitar generar imágenes muy grandes)
    const allowedSizes = ["512x512", "768x512", "1024x768", "1024x1024"];
    const validSize = allowedSizes.includes(size) ? size : "1024x768";

    const response = await apiClient.post("/api/generate-image", {
      prompt,
      size: validSize,
      style,
      subject,
      quality,
      accessibleDescription,
    });

    // Validación más completa de la respuesta
    if (!response.data) {
      throw new Error("Respuesta vacía del servidor");
    }

    if (!response.data.imageUrl) {
      throw new Error("No se recibió URL de imagen");
    }

    // Incluir metadatos adicionales si están disponibles
    return {
      imageUrl: response.data.imageUrl,
      altText: response.data.accessibleDescription || `Imagen sobre ${subject}`,
      metadata: response.data.metadata || {
        prompt,
        style,
        size: validSize,
        generatedAt: new Date().toISOString(),
      },
    };
  } catch (error) {
    console.error("Error al generar imagen:", error);

    let errorMessage = "Error al generar la imagen";

    if (error.code === "ECONNABORTED") {
      errorMessage =
        "La generación de la imagen está tomando demasiado tiempo. Intenta con una descripción más simple.";
    } else if (!navigator.onLine) {
      errorMessage =
        "No hay conexión a internet. Verifica tu conexión e inténtalo de nuevo.";
    } else if (error.response?.status === 429) {
      errorMessage =
        "Has alcanzado el límite de generación de imágenes. Intenta de nuevo más tarde.";
    } else if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Crea un post completo con texto e imagen
 * @param {Object|string} options - Opciones o tema del post
 * @returns {Promise<Object>} - Post completo con texto e imagen
 */
export const createCompletePost = async (options) => {
  try {
    // Configuración del tipo de contenido
    const postOptions =
      typeof options === "string" ? { topic: options } : options;

    // 1. Generar contenido de texto
    const postContent = await generateBlogPost(postOptions);

    // 2. Generar prompt para imagen
    const imagePrompt = `Imagen profesional ilustrativa sobre ${
      postOptions.topic
    } relacionado con ${
      postOptions.audience || "síndrome de Down"
    }, mostrando aspectos positivos y empoderamiento`;

    // 3. Generar imagen
    const imageResult = await generateImage(imagePrompt, {
      style: postOptions.imageStyle || "realistic",
      subject: postOptions.audience || "síndrome de Down",
      accessibleDescription: true,
    });

    // 4. Devolver post completo con todos los metadatos
    return {
      ...postContent,
      imageUrl: imageResult.imageUrl,
      imageAltText: imageResult.altText,
      createdAt: new Date().toISOString(),
      status: "draft",
      seoData: {
        title: postContent.title,
        description: postContent.summary,
        keywords: postContent.keywords,
        imageAlt: imageResult.altText,
      },
    };
  } catch (error) {
    console.error("Error al crear post completo:", error);
    throw new Error(`No se pudo crear el post completo: ${error.message}`);
  }
};

/**
 * Estima el tiempo de lectura de un texto
 * @param {string} text - Texto a analizar
 * @returns {number} - Tiempo estimado de lectura en minutos
 * @private
 */
function estimateReadingTime(text) {
  if (!text) return 0;
  // Persona promedio lee ~200 palabras por minuto
  const wordCount = text.split(/\s+/).length;
  return Math.ceil(wordCount / 200);
}

/**
 * Estima la complejidad de un texto
 * @param {string} text - Texto a analizar
 * @returns {string} - Nivel de complejidad (bajo, medio, alto)
 * @private
 */
function estimateTextComplexity(text) {
  if (!text) return "medium";

  // Análisis simple: longitud de oraciones y palabras
  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  const words = text.split(/\s+/);

  if (sentences.length === 0) return "medium";

  // Longitud promedio de oraciones
  const avgSentenceLength = words.length / sentences.length;

  // Clasificación simple
  if (avgSentenceLength < 10) {
    return "bajo";
  } else if (avgSentenceLength > 20) {
    return "alto";
  } else {
    return "medio";
  }
}

/**
 * Obtiene una respuesta del chatbot optimizada para necesidades especiales
 * @param {string} message - Mensaje del usuario
 * @param {Object} [options={}] - Opciones de accesibilidad
 * @returns {Promise<Object>} - Respuesta adaptada
 */
export const getAccessibleResponse = async (message, options = {}) => {
  const {
    simplifiedLanguage = true,
    textToSpeech = true,
    readingLevel = "basic",
    language = "es",
  } = options;

  try {
    const response = await getChatbotResponse(message, "", language, {
      simplifiedLanguage,
      textToSpeech,
      accessibilityPreferences: {
        readingLevel,
        useVisualCues: true,
        avoidComplexTerms: true,
      },
    });

    return response;
  } catch (error) {
    console.error("Error al obtener respuesta accesible:", error);
    throw error;
  }
};
